/**
 * Verdict mutations — Evaluate (judge the day on the clock AND act on it:
 * burns, mid-day stops), Reset pool and Re-pick the list (21/10/2026). Split from the config
 * mutations: these are operational acts on the day, not edits. Both
 * invalidate the root key so the preview, report and feed re-read together.
 * The DEV simulation lives in simulation/useSimulation.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/services/api';

import { dropGeneratorKeys } from './useDropGenerator';

const BASE = '/drop-generator/verdicts';

/** What a re-pick did: pairs forgotten, cursor rows cleared, today re-synced. */
export interface RepickOutcome {
  pairs: number;
  cleared: number;
  resynced: boolean;
}

export function useVerdictMutations() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: dropGeneratorKeys.all });

  const evaluateVerdicts = useMutation({
    mutationFn: async (input: { countryKey: string; at?: string | null }) => {
      // The report is re-read through useVerdictReport — no typed body here.
      await api.post(
        `${BASE}/${input.countryKey}/evaluate`,
        input.at ? { at: input.at } : {},
      );
    },
    onSuccess: invalidate,
  });

  const resetPool = useMutation({
    mutationFn: async (input: {
      countryKey: string;
      containerId: string;
      listGroupId: string;
    }) => {
      await api.post(
        `${BASE}/${input.countryKey}/containers/${input.containerId}/groups/${input.listGroupId}/reset-pool`,
      );
    },
    onSuccess: invalidate,
  });

  /** 21/10/2026 — Re-pick the list: one group of the container, or every group (listGroupId null). */
  const repickList = useMutation({
    mutationFn: async (input: { countryKey: string; containerId: string; listGroupId: string | null }) => {
      const path = input.listGroupId ? `groups/${input.listGroupId}/repick` : 'repick';
      const res = await api.post<{ data: RepickOutcome }>(`${BASE}/${input.countryKey}/containers/${input.containerId}/${path}`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });

  return { evaluateVerdicts, resetPool, repickList };
}
